import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { GlassCard } from "./GlassCard";
import { 
  Bell, 
  BellOff, 
  Calendar, 
  Clock, 
  Download,
  Sparkles,
  CheckCircle
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DAYS = [
  { value: 0, label: "Lunes" },
  { value: 1, label: "Martes" },
  { value: 2, label: "Miércoles" },
  { value: 3, label: "Jueves" },
  { value: 4, label: "Viernes" },
  { value: 5, label: "Sábado" },
  { value: 6, label: "Domingo" },
];

const TIMES = [
  "08:00", "09:00", "10:00", "11:00", "12:00",
  "13:00", "14:00", "15:00", "16:00", "17:00",
  "18:00", "19:00", "20:00", "21:00"
];

export const MoodStoryNotifications = () => {
  const [settings, setSettings] = useState({
    enabled: true,
    day: 6, // Sunday
    time: "10:00"
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/user/mood-story-notifications`);
      setSettings(response.data);
    } catch (error) {
      console.error("Error fetching settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/user/mood-story-notifications`, settings);
      toast.success("Configuración guardada");
      setHasChanges(false);
    } catch (error) {
      toast.error("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const downloadCalendar = () => {
    window.open(`${API}/user/mood-story-calendar.ics`, "_blank");
  };

  if (loading) {
    return (
      <GlassCard className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-white/10 rounded w-1/2" />
          <div className="h-10 bg-white/10 rounded" />
          <div className="h-10 bg-white/10 rounded" />
        </div>
      </GlassCard>
    );
  }

  return (
    <GlassCard className="p-6" data-testid="mood-story-notifications">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-neon-pink/20 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-neon-pink" />
          </div>
          <div>
            <h3 className="font-rajdhani font-bold text-lg text-white">
              Notificaciones de Mood Story
            </h3>
            <p className="text-xs text-slate-400">
              Recibe un recordatorio semanal para ver tu historia
            </p>
          </div>
        </div>
        
        {/* Toggle */}
        <button
          onClick={() => handleChange("enabled", !settings.enabled)}
          className={`relative w-14 h-7 rounded-full transition-colors ${
            settings.enabled ? "bg-neon-pink" : "bg-slate-600"
          }`}
          data-testid="toggle-mood-story-notifications"
        >
          <motion.div
            className="absolute top-1 w-5 h-5 bg-white rounded-full shadow"
            animate={{ left: settings.enabled ? "calc(100% - 24px)" : "4px" }}
            transition={{ type: "spring", stiffness: 500, damping: 30 }}
          />
        </button>
      </div>

      {settings.enabled && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          className="space-y-4"
        >
          {/* Day selector */}
          <div>
            <label className="block text-sm text-slate-400 mb-2 flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Día de la semana
            </label>
            <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
              {DAYS.map(day => (
                <button
                  key={day.value}
                  onClick={() => handleChange("day", day.value)}
                  className={`px-3 py-2 rounded-lg text-sm font-rajdhani transition-all ${
                    settings.day === day.value
                      ? "bg-neon-pink/30 border-neon-pink/50 text-neon-pink"
                      : "bg-white/5 border-white/10 text-slate-400 hover:bg-white/10"
                  } border`}
                >
                  {day.label.slice(0, 3)}
                </button>
              ))}
            </div>
          </div>

          {/* Time selector */}
          <div>
            <label className="block text-sm text-slate-400 mb-2 flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Hora
            </label>
            <select
              value={settings.time}
              onChange={(e) => handleChange("time", e.target.value)}
              className="w-full px-4 py-2 rounded-lg bg-black/30 border border-white/10 text-white font-rajdhani focus:border-neon-pink/50 focus:outline-none"
            >
              {TIMES.map(time => (
                <option key={time} value={time}>{time}</option>
              ))}
            </select>
          </div>

          {/* Info box */}
          <div className="p-4 rounded-lg bg-neon-pink/10 border border-neon-pink/20">
            <div className="flex items-start gap-3">
              <Bell className="w-5 h-5 text-neon-pink flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-white">
                  Recibirás un recordatorio cada <span className="text-neon-pink font-semibold">{DAYS.find(d => d.value === settings.day)?.label}</span> a las <span className="text-neon-pink font-semibold">{settings.time}</span>
                </p>
                <p className="text-xs text-slate-400 mt-1">
                  Para ver el resumen de tu semana emocional
                </p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <button
              onClick={handleSave}
              disabled={saving || !hasChanges}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-rajdhani font-semibold transition-all ${
                hasChanges
                  ? "bg-neon-pink/20 text-neon-pink border border-neon-pink/50 hover:bg-neon-pink/30"
                  : "bg-white/5 text-slate-500 border border-white/10 cursor-not-allowed"
              }`}
              data-testid="save-mood-story-settings"
            >
              {saving ? (
                <div className="w-5 h-5 border-2 border-neon-pink/30 border-t-neon-pink rounded-full animate-spin" />
              ) : (
                <CheckCircle className="w-5 h-5" />
              )}
              {saving ? "Guardando..." : hasChanges ? "Guardar cambios" : "Sin cambios"}
            </button>
            
            <button
              onClick={downloadCalendar}
              className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-white/5 text-white border border-white/10 hover:bg-white/10 font-rajdhani transition-all"
              data-testid="download-mood-story-calendar"
            >
              <Download className="w-5 h-5" />
              Añadir al calendario
            </button>
          </div>
        </motion.div>
      )}

      {!settings.enabled && (
        <div className="flex items-center gap-3 p-4 rounded-lg bg-white/5 border border-white/10">
          <BellOff className="w-5 h-5 text-slate-400" />
          <p className="text-sm text-slate-400">
            Las notificaciones de Mood Story están desactivadas
          </p>
        </div>
      )}
    </GlassCard>
  );
};

export default MoodStoryNotifications;
